import clsx from "clsx";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import Button from "../../ui/molecules/button";
import { Spinner } from "../../ui/molecules/spinner";

import z from "zod";
import { useCopyTargetListMutation, useFindOneTargetListQuery } from "../../../features/targetlistApi";
import { addBannerWithTimeout } from "../../lib/centralizedBanner/slice";
import { colors } from "../../ui/colors";
import { PlusIcon } from "../../ui/icons/plus";
import { newTargetListSchema } from "./editTargetListDetails/validation";
import { SelectProject } from "./selectProject";
import { ContactType } from "../../lib/filters/dto";

interface Props {
  visible: boolean;
  onClose: () => void;
  targetId: string | undefined;
}

export function CopyTargetListToProjectModal({ visible, onClose, targetId }: Props) {
  const { data, isLoading: isLoadingTarget } = useFindOneTargetListQuery(targetId!, {
    skip: !visible && !targetId,
  });

  const [copyTargetList, { isLoading }] = useCopyTargetListMutation();

  const dispatch = useAppDispatch();

  const [value, setValue] = useState<{ label: string, value: string } | undefined>(undefined)

  const handleCopyTargetList = async () => {
    try {
      const target = data?.targetList
      const response = await copyTargetList({
        targetList: {
          projectId: value?.value!,
          name: target?.name!,
          commercialIds: target?.commercialIds ?? [],
          departments: target?.departments ?? [],
          roles: target?.roles ?? [],
          revenue: target?.revenue!,
          employeesNumber: target?.employeesNumber!,
          sectors: target?.sectors ?? [],
          categories: target?.categories ?? [],
        },
        people: data?.contactsIds?.find(contacts => contacts.contactType === ContactType.PERSON)?.dto.map(contact => contact.contactId!) || [],
        companies: data?.contactsIds?.find(contacts => contacts.contactType === ContactType.COMPANY)?.dto.map(contact => contact.contactId!) || []
      }).unwrap()
      window.location.replace('/edit-target-list/' + response.targetListId + '/specifics/' + value?.value);
      onClose()
      dispatch(addBannerWithTimeout({
        message: "Lista target copiata con successo!",
        variant: "success",
      }))
    } catch (error) {
      const _error = error as {
        status: number;
        data: {
          errors: { message: string }[];
        }
      }
      let message = "Si è verificato un errore durante la copia della lista target."
      if (_error.data.errors && _error.data.errors.length > 0) {
        if (_error.data.errors.map(e => e.message).find(message => message.includes("ID parameter is empty or not valid"))) {
          message = "Esiste già una lista target con lo stesso nome in questo progetto."
        }
      }
      dispatch(addBannerWithTimeout({
        message,
        variant: "error",
      }))
    }
  }

  return (
    <div
      onClick={() => onClose()}
      className={clsx(
        "transition-all fixed bg-opacity-30 bg-neutral-950 backdrop-blur-sm top-0 left-0 h-full w-full flex items-center justify-center z-[100]",
        {
          block: visible,
          hidden: !visible,
        }
      )}
    >
      {
        isLoading || isLoadingTarget ? (
          <Spinner />
        ) : (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="transition-all relative flex flex-col items-center bg-white rounded-[12px] opacity-100 shadow-sm min-w-[400px]"
          >
            <div
              onClick={() => onClose()}
              className="absolute cursor-pointer rotate-45 top-[-16px] right-[-16px] border border-brandPrimary-600 h-8 w-8 rounded-full flex items-center justify-center bg-brandPrimary-50"
            >
              <PlusIcon color={colors.brandPrimary[600]} size={16} />
            </div>
            <div className="p-6 text-heading-xs font-semibold text-center">
              Copia lista target
            </div>
            <div className="px-6 py-8 h-full flex flex-col justify-center items-center gap-6 border-t-neutral-200 border-t">
              <span className="text-text-md text-center font-normal text-neutral-600">
                Vuoi copiare questa lista target in un altro progetto?
                <br /> Seleziona il progetto in cui copiarla e clicca "Conferma" per continuare.
              </span>
              <div className="flex flex-col w-full h-full gap-3">
                <SelectProject
                  project={value}
                  setProject={function (value?: { label: string; value: string; }): void {
                    setValue(value)
                  }}
                />
              </div>
              <div className="w-full flex flex-1 gap-3 mt-3">
                <div className="w-1/2">
                  <Button
                    fullWidth
                    size={"sm"}
                    iconPosition={"off"}
                    variant={"outline"}
                    color={"gray"}
                    label="Annulla"
                    onClick={() => onClose()}
                  />
                </div>
                <div className="w-1/2">
                  <Button
                    disabled={value === undefined}
                    fullWidth
                    size={"sm"}
                    iconPosition={"off"}
                    variant={"solid"}
                    color={"blue"}
                    label="Conferma"
                    onClick={() => handleCopyTargetList()}
                  //TODO handle copy target list
                  // onClick={() => {
                  //   dispatch(copyTargetList({
                  //     data: {
                  //       projectId: value?.value!,
                  //       name: targetListState.findTargetListResponse?.name!,
                  //       commercialIds: targetListState.findTargetListResponse?.commercialIds!,
                  //       departments: targetListState.findTargetListResponse?.departments!,
                  //       roles: targetListState.findTargetListResponse?.roles!,
                  //       revenue: targetListState.findTargetListResponse?.revenue!,
                  //       employeesNumber: targetListState.findTargetListResponse?.employeesNumber!,
                  //       sectors: targetListState.findTargetListResponse?.sectors!,
                  //       categories: targetListState.findTargetListResponse?.categories!,
                  //       particularityId: targetListState.findTargetListResponse?.particularity?.id!
                  //     },
                  //     people: contactsState.getContactsIdsResponse?.find(contacts => contacts.contactType === ContactType.PERSON)?.dto.map(contact => contact.contactId)!,
                  //     companies: contactsState.getContactsIdsResponse?.find(contacts => contacts.contactType === ContactType.COMPANY)?.dto.map(contact => contact.contactId)!,
                  //   }))
                  // }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
